import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Badge, Dropdown, useDateTimeFormat, useLocalizeMessage } from 'libs.nucleus';
import { EM_DASH } from 'libs.react';
import { isWallclockDate } from '../site_document_details.helpers';
/**
 * Invite statuses are a wider set than document statuses and arrive as raw Cortex
 * enums, so they get their own label and colour mapping.
 */
const INVITE_STATUS_LABELS = {
    complete: 'label.site_document_details.invite_status.complete',
    connected: 'label.site_document_details.invite_status.connected',
    pending: 'label.site_document_details.invite_status.pending',
    sent: 'label.site_document_details.invite_status.sent',
    cancelled: 'label.site_document_details.invite_status.cancelled',
    voided: 'label.site_document_details.invite_status.voided',
};
const INVITE_STATUS_VARIANTS = {
    complete: 'success',
    connected: 'success',
    pending: 'incomplete',
    sent: 'incomplete',
    cancelled: 'danger',
    voided: 'common',
};
export const useDocumentSignersTableColumns = ({ rows = [], showRowActions = false, onOpenSignerInvite, canSignNowFor, onSignNow, } = {}) => {
    const translate = useLocalizeMessage();
    const { formatDateTime, formatPlainDate } = useDateTimeFormat();
    const signersColumns = useMemo(() => [
        {
            header: translate('label.site_document_details.signers.order'),
            accessorKey: 'order',
            cell: (context) => context.row.original.order ?? EM_DASH,
        },
        {
            header: translate('label.site_document_details.signers.email'),
            accessorKey: 'email',
            cell: (context) => context.row.original.email || EM_DASH,
        },
        {
            header: translate('label.site_document_details.signers.signer_role'),
            accessorKey: 'signerRole',
            cell: (context) => context.row.original.signerRole || EM_DASH,
        },
        {
            header: translate('label.site_document_details.signers.status'),
            accessorKey: 'status',
            cell: (context) => {
                const { status } = context.row.original;
                if (!status) {
                    return EM_DASH;
                }
                const key = status.toLowerCase();
                const labelKey = INVITE_STATUS_LABELS[key];
                return (_jsx(Badge, { variant: INVITE_STATUS_VARIANTS[key] ?? 'common', label: labelKey ? translate(labelKey) : status }));
            },
        },
        {
            header: translate('label.site_document_details.signers.completion_date_time'),
            accessorKey: 'completionTime',
            cell: (context) => {
                const { completionTime } = context.row.original;
                if (!completionTime) {
                    return EM_DASH;
                }
                // Accepted-signer times are instants, but a bare date must not reach the
                // instant formatter — it would render the previous day at negative offsets.
                return isWallclockDate(completionTime) ? formatPlainDate(completionTime) : formatDateTime(completionTime);
            },
        },
        ...(showRowActions && onOpenSignerInvite
            ? [
                {
                    id: 'actions',
                    header: () => _jsx("span", { className: 'nucleus-sr-only', children: translate('generic.actions') }),
                    size: 48,
                    cell: (context) => {
                        const row = context.row.original;
                        const dropdownItems = [
                            {
                                id: 'invite',
                                // Classic titled this by what it would do to the row: a role with no
                                // invite gets assigned, one with an invite gets re-sent.
                                content: row.isUnassigned
                                    ? translate('action.site_document_details.assign_signer')
                                    : translate('action.site_document_details.resend_invite'),
                                onClick: () => onOpenSignerInvite(row),
                            },
                        ];
                        if (onSignNow && canSignNowFor?.(row, rows)) {
                            dropdownItems.push({
                                id: 'sign-now',
                                content: translate('action.site_document_details.sign_now'),
                                onClick: () => void onSignNow(row),
                            });
                        }
                        return (_jsx("div", { className: 'flex justify-end', children: _jsx(Dropdown, { variant: 'kebab', items: dropdownItems }) }));
                    },
                },
            ]
            : []),
    ], [canSignNowFor, formatDateTime, formatPlainDate, onOpenSignerInvite, onSignNow, rows, showRowActions, translate]);
    return { signersColumns };
};
